import { useQuery } from '@tanstack/react-query';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  REPORT_VERSION_STATUS,
  type ReportVersionStatus,
} from '@lims/shared-types';
import {
  reportsApi,
  type ReportDetailView,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  Alert,
  DataTable,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  ReadOnlyField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { isolateRuns, run } from '../settings/report-template-parts.js';
import { DraftActions, VersionFileDownload } from './report-draft-actions.js';
import {
  DeliveryActions,
  NoticeForParty,
  ReleasePaymentHoldAction,
} from './report-delivery-actions.js';
import { LifecycleActions } from './report-lifecycle-actions.js';
import { PortalReleasePanel } from './report-portal-actions.js';
import {
  DeliveryStatusBadge,
  MomentText,
  PersonAt,
  REPORTS_QUERY_KEY,
  ReportNumber,
  ReportStatusBadge,
  ResultCorrectionState,
  VersionStatusBadge,
  bilingual,
} from './report-parts.js';
import { RecordStrip, StripItem } from './register-panel.js';

type ReportVersion = NonNullable<ReportDetailView['currentVersion']>;
type ReportResult = ReportVersion['results'][number];

/** Statuses that mean a file was rendered and stored for the version. */
const ISSUED_EVER: readonly ReportVersionStatus[] = [
  REPORT_VERSION_STATUS.ISSUED_VALID,
  REPORT_VERSION_STATUS.SUSPENDED,
  REPORT_VERSION_STATUS.SUPERSEDED,
  REPORT_VERSION_STATUS.CANCELLED,
];

/**
 * ONE REPORT — Phase 10.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4, 5 AND 8 BEFORE ADDING ANYTHING TO THIS PAGE.
 * ===========================================================================
 *
 * WHAT IS SHOWN SIDE BY SIDE, AND NEVER MERGED: the report's status and its
 * delivery state (issuance is not delivery); the version in force and the
 * draft being prepared; the payment hold and the report's validity (a hold
 * changes neither). Every issued fact — number, version, date, pages, the
 * file's fingerprint — is read from what was frozen at issuance, and nothing
 * on this page recalculates it.
 *
 * NO SCIENCE. A result is named by its test and sample, who performed, reviewed
 * and approved it, and what has happened to it since it was reported (D117). Its
 * content is printed on the report verbatim from the record and is not
 * restated, summarised or judged here. Approval is not a pass.
 *
 * EVERY ACTION IS OFFERED FROM THE SERVER'S `can.*` HINTS and re-checked by the
 * server on every request (section 5.1).
 */
export function ReportDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();

  const detail = useQuery({
    queryKey: [...REPORTS_QUERY_KEY, 'detail', id],
    queryFn: () => reportsApi.get(id),
    enabled: id !== '',
  });

  if (detail.isLoading) return <LoadingState />;
  if (detail.error || !detail.data) {
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  }

  const report = detail.data;
  const title = report.reportNumber ?? t('reports.numberPending');
  // A version that was ever issued, whatever it is now. NOT `currentVersion`:
  // that is only a version in force (valid or suspended), so a cancelled report
  // has none and was announced as "never issued, no number and no file" above
  // its own number and file — found in the Phase 10 browser check.
  const everIssued = report.versions.some((version) => version.issuedAt !== null);
  const anyAction = Object.values(report.can).some(Boolean);

  return (
    <>
      <PageHeader
        title={title}
        subtitle={t('reports.subtitle')}
        breadcrumbs={[{ label: t('nav.reports'), to: '/reports' }, { label: title }]}
      />

      <div className="flex flex-col gap-gutter">
        {/*
          The record bar (D278): the report's status and its delivery state as
          two labelled facts, side by side and never merged — issuance is not
          delivery. A report never issued has nothing to hand over, so it shows
          no delivery state at all.
        */}
        <RecordStrip>
          <StripItem label={t('reports.columns.status')}>
            <ReportStatusBadge status={report.status} />
          </StripItem>
          {everIssued ? (
            <StripItem label={t('reports.columns.delivery')}>
              <DeliveryStatusBadge status={report.deliveryStatus} />
            </StripItem>
          ) : null}
          {report.currentVersion ? (
            <StripItem label={t('reports.detail.currentVersion')}>
              <span className="measure">
                {t('reports.detail.version', { number: report.currentVersion.versionNumber })}
              </span>
            </StripItem>
          ) : null}
        </RecordStrip>

        {everIssued ? (
          <Alert
            tone="info"
            title={t('reports.issuanceNotDeliveryTitle')}
            description={t('reports.issuanceNotDeliveryBody')}
          />
        ) : (
          <Alert tone="neutral" title={t('reports.detail.notIssuedYet')} />
        )}

        {anyAction ? null : (
          <Alert
            tone="neutral"
            title={t('reports.detail.noAction')}
            description={t('reports.detail.noActionHint')}
          />
        )}

        {/*
          Two columns on a wide screen: the versions, their results and the
          handover in the main column; the facts, the payment hold and the
          history in the side column. On a phone they stack, main first.
        */}
        <div className="grid items-start gap-gutter lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="flex min-w-0 flex-col gap-gutter">
            {/* Correct, suspend, reinstate, cancel — each from its `can.*` hint (sub-phase 10c). */}
            <LifecycleActions report={report} />

            {report.currentVersion ? (
              <VersionPanel
                report={report}
                version={report.currentVersion}
                heading={t('reports.detail.currentVersion')}
              />
            ) : null}

            {report.draftVersion ? (
              <VersionPanel
                report={report}
                version={report.draftVersion}
                heading={t('reports.detail.draftVersion')}
                isDraft
              />
            ) : everIssued ? (
              <p className="text-caption text-default-600">{t('reports.detail.noDraft')}</p>
            ) : null}

            <DeliveriesPanel report={report} />
            {/* Release to / withdraw from the client portal (Phase 11, D149). */}
            <PortalReleasePanel report={report} />
            <AffectedPartiesPanel report={report} />
          </div>

          <div className="flex min-w-0 flex-col gap-gutter">
            <OverviewPanel report={report} />
            <PaymentHoldPanel report={report} />
            <VersionHistoryPanel report={report} />
            <EventsPanel report={report} />
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function OverviewPanel({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();
  const { can } = useAuth();

  // Status and delivery state sit in the record bar above; this panel holds
  // the report's identity and the records it belongs to.
  return (
    <Panel title={t('reports.detail.overview')}>
      <div className="grid grid-cols-1 gap-stack">
        <div className="min-w-0">
          <ReadOnlyField label={t('reports.detail.number')}>
            <span className="measure">
              <ReportNumber number={report.reportNumber} />
            </span>
          </ReadOnlyField>
          {report.reportNumber ? null : (
            <p className="mt-1 text-caption text-default-600">{t('reports.numberPendingHint')}</p>
          )}
        </div>
        <ReadOnlyField label={t('reports.detail.workOrder')}>
          {can(PERMISSIONS.WORK_ORDERS_VIEW) ? (
            <Link
              to={`/work-orders/${report.workOrder.id}`}
              className="measure text-primary-700 underline-offset-2 hover:underline"
              dir="ltr"
            >
              {report.workOrder.number}
            </Link>
          ) : (
            <span className="measure" dir="ltr">
              {report.workOrder.number}
            </span>
          )}
        </ReadOnlyField>
        <ReadOnlyField label={t('reports.detail.client')}>
          <bdi>{report.client.name ?? t('common.notRecorded')}</bdi>
        </ReadOnlyField>
        <ReadOnlyField label={t('reports.detail.project')}>
          <bdi>
            {report.project
              ? (report.project.name ?? t('common.notRecorded'))
              : t('common.none')}
          </bdi>
        </ReadOnlyField>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Payment hold — staff view only
// ---------------------------------------------------------------------------

function PaymentHoldPanel({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();
  const hold = report.paymentHold;

  return (
    <Panel title={t('reports.paymentHold.title')}>
      <div className="flex flex-col gap-stack">
        <div>
          <StatusBadge
            label={t(`paymentHold.${hold.state}`)}
            tone={hold.state === 'held' ? 'warning' : 'neutral'}
          />
          <p className="mt-1 text-caption text-default-600">{t(`paymentHoldHint.${hold.state}`)}</p>
        </div>
        {hold.state === 'none' ? null : (
          <>
            <Alert
              tone="info"
              title={t('reports.paymentHold.changesNothingTitle')}
              description={t('reports.paymentHold.changesNothingBody')}
            />
            <p className="text-caption text-default-600">{t('reports.paymentHold.staffUnaffected')}</p>
            <p className="text-caption text-default-600">{t('reports.paymentHold.neverDisclosed')}</p>
          </>
        )}
        {hold.state === 'released' ? (
          <div className="flex flex-col gap-1 border-t border-default-100 pt-3">
            <p className="text-caption text-default-700">
              {isolateRuns(
                t('reports.paymentHold.releasedBy', { name: run('name'), date: run('date') }),
                {
                  name: hold.releasedBy?.displayName ?? t('common.notRecorded'),
                  date: <MomentText at={hold.releasedAt} />,
                },
              )}
            </p>
            <ReadOnlyField label={t('reports.paymentHold.releaseNote')}>
              <bdi className="whitespace-pre-wrap">{hold.releaseNote ?? t('common.notRecorded')}</bdi>
            </ReadOnlyField>
            {/* Phase 11 (D151): a hold is released by hand, or by the work order being settled. */}
            {hold.basis ? (
              <p className="text-caption text-default-600">
                {hold.basis === 'work_order_settled'
                  ? t('reports.paymentHold.basisWorkOrderSettled')
                  : t('reports.paymentHold.basisManual')}
              </p>
            ) : null}
          </div>
        ) : null}
        {/* Release the hold, from `can.releasePaymentHold` (sub-phase 10c). */}
        <ReleasePaymentHoldAction report={report} />
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// A version: the one in force, or the draft
// ---------------------------------------------------------------------------

function VersionPanel({
  report,
  version,
  heading,
  isDraft = false,
}: {
  readonly report: ReportDetailView;
  readonly version: ReportVersion;
  readonly heading: string;
  readonly isDraft?: boolean;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const templateName = bilingual(version.template.name, locale) ?? t('common.notRecorded');
  const issued = ISSUED_EVER.includes(version.status);

  return (
    <Panel
      title={heading}
      {...(isDraft ? { actions: <VersionStatusBadge status={version.status} /> } : {})}
    >
      <div className="flex flex-col gap-gutter">
        {isDraft ? <DraftActions report={report} /> : null}

        {/*
          The issued document's two acts (D330): download it in Arabic or in
          English — each named, never chosen from the interface language — and
          open its public verification page. The fingerprint and the raw
          address stay on the record; the page does not show them.
        */}
        {issued && (report.can.downloadFile || version.verificationUrl) ? (
          <div className="flex flex-wrap items-start gap-inline" data-testid="issued-actions">
            {report.can.downloadFile ? (
              <VersionFileDownload
                report={report}
                versionNumber={version.versionNumber}
                languages={version.downloadLanguages}
              />
            ) : null}
            {version.verificationUrl ? <VerifyReportLink url={version.verificationUrl} /> : null}
          </div>
        ) : null}

        {version.correction ? (
          <div className="flex flex-col gap-inline rounded-medium border border-default-200 p-3">
            <p className="text-body font-medium text-default-900">{t('reports.correction.details')}</p>
            <div className="grid gap-stack sm:grid-cols-2">
              <ReadOnlyField label={t('reports.correction.impact')}>
                {t(`correctionImpact.${version.correction.impact}`)}
              </ReadOnlyField>
              <ReadOnlyField label={t('reports.correction.reason')}>
                <bdi className="whitespace-pre-wrap">{version.correction.reason}</bdi>
              </ReadOnlyField>
              <ReadOnlyField label={t('reports.correction.requestedChange')}>
                <bdi className="whitespace-pre-wrap">{version.correction.requestedChange}</bdi>
              </ReadOnlyField>
            </div>
            <p className="text-caption text-default-700">
              {isolateRuns(
                t('reports.correction.openedBy', { name: run('name'), date: run('date') }),
                {
                  name: version.correction.openedBy.displayName ?? t('common.notRecorded'),
                  date: <MomentText at={version.correction.openedAt} />,
                },
              )}
            </p>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-stack sm:grid-cols-2 xl:grid-cols-3">
          <ReadOnlyField label={t('reports.columns.version')}>
            <span className="tabular-nums">{t('reports.detail.version', { number: version.versionNumber })}</span>
          </ReadOnlyField>
          {isDraft ? null : (
            <div className="min-w-0">
              <p className="text-caption text-default-600">{t('reports.columns.status')}</p>
              <div className="mt-1">
                <VersionStatusBadge status={version.status} />
              </div>
            </div>
          )}
          <ReadOnlyField label={t('reports.detail.template')}>
            <bdi>{templateName}</bdi>
          </ReadOnlyField>
          <ReadOnlyField
            label={issued ? t('reports.detail.issuedLanguage') : t('reports.detail.defaultLanguage')}
          >
            {t(`reportLanguage.${version.language}`)}
          </ReadOnlyField>
          <ReadOnlyField label={t('reports.detail.preparedBy')}>
            <PersonAt person={version.preparedBy} at={version.preparedAt} />
          </ReadOnlyField>
          {issued ? (
            <>
              <ReadOnlyField label={t('reports.detail.issuedBy')}>
                <PersonAt person={version.issuedBy} />
              </ReadOnlyField>
              <ReadOnlyField label={t('reports.detail.issuedAt')}>
                <MomentText at={version.issuedAt} />
              </ReadOnlyField>
            </>
          ) : null}
        </div>

        <div className="min-w-0">
          <ReadOnlyField label={t('reports.detail.remarks')}>
            {/* The laboratory's own words, verbatim. */}
            <bdi className="whitespace-pre-wrap">{version.remarks ?? t('reports.detail.noRemarks')}</bdi>
          </ReadOnlyField>
        </div>

        <ResultsTable version={version} />
      </div>
    </Panel>
  );
}

/**
 * The public verification page of an issued version, as one clear action
 * (D330). It opens the very page the printed QR opens — in a new tab, with no
 * opener and no referrer — and the raw address is not printed on the page.
 */
function VerifyReportLink({ url }: { readonly url: string }) {
  const { t } = useTranslation();
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
      data-testid="verify-report"
      className="inline-flex min-h-8 items-center rounded-medium border border-default-300 px-3 text-caption font-medium text-primary-700 underline-offset-2 hover:underline"
    >
      {t('reports.detail.verifyReport')}
      <span className="sr-only"> {t('reports.detail.opensInNewTab')}</span>
    </a>
  );
}

function ResultsTable({ version }: { readonly version: ReportVersion }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();
  const rows = [...version.results].sort((first, second) => first.sortOrder - second.sortOrder);
  const mayOpenHistory = can(PERMISSIONS.RESULTS_VIEW);

  const columns: readonly DataColumn<ReportResult>[] = [
    {
      key: 'test',
      header: t('reports.detail.resultColumns.test'),
      render: (row) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-default-900">
            <span className="tabular-nums text-default-600">{rows.indexOf(row) + 1}</span>
            {' · '}
            <bdi className="font-medium">{bilingual(row.test.name, locale) ?? t('common.notRecorded')}</bdi>
          </span>
          <span className="text-caption text-default-600">
            {t('reports.detail.resultColumns.sample')}
            {': '}
            <span className="measure" dir="ltr">
              {row.sample.number}
            </span>
          </span>
          <span className="text-caption text-default-600">
            {t('reports.detail.resultColumns.revision')}
            {': '}
            <span className="tabular-nums">{row.revisionNumber}</span>
          </span>
          {row.correctionAffected ? (
            <StatusBadge label={t('reports.detail.correctionAffected')} tone="warning" />
          ) : null}
          {mayOpenHistory ? (
            <Link
              to={`/attempts/${row.attemptId}`}
              className="text-caption text-primary-700 underline-offset-2 hover:underline"
            >
              {t('reports.detail.openResultHistory')}
            </Link>
          ) : null}
        </div>
      ),
    },
    {
      key: 'people',
      header: t('reports.detail.resultColumns.approvedBy'),
      render: (row) => (
        <dl className="grid min-w-0 gap-1 text-caption">
          <div>
            <dt className="text-default-600">{t('reports.detail.resultColumns.performedBy')}</dt>
            <dd className="text-default-800">
              <PersonAt person={row.performedBy} />
            </dd>
          </div>
          <div>
            <dt className="text-default-600">{t('reports.detail.resultColumns.reviewedBy')}</dt>
            <dd className="text-default-800">
              <PersonAt person={row.reviewedBy} />
            </dd>
          </div>
          <div>
            <dt className="text-default-600">{t('reports.detail.resultColumns.approvedBy')}</dt>
            <dd className="text-default-800">
              <PersonAt person={row.approvedBy} />
            </dd>
          </div>
          <div>
            <dt className="text-default-600">{t('reports.detail.resultColumns.approvedAt')}</dt>
            <dd className="text-default-800">
              <MomentText at={row.approvedAt} />
            </dd>
          </div>
        </dl>
      ),
    },
    {
      key: 'state',
      header: t('reports.detail.resultColumns.resultState'),
      render: (row) => <ResultCorrectionState state={row.resultCorrection} />,
    },
  ];

  return (
    <div className="flex flex-col gap-inline">
      <div>
        <h3 className="text-body font-medium text-default-900">{t('reports.detail.results')}</h3>
        <p className="mt-1 text-caption text-default-600">{t('reports.detail.resultsHint')}</p>
      </div>
      <DataTable
        label={t('reports.detail.results')}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.attemptId}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

function VersionHistoryPanel({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();

  return (
    <Panel title={t('reports.detail.versions')} description={t('reports.detail.versionsHint')}>
      <ul className="flex flex-col divide-y divide-default-100" data-testid="version-history">
        {report.versions.map((version) => {
          const issued = ISSUED_EVER.includes(version.status);
          return (
            <li
              key={version.id}
              data-version={version.versionNumber}
              className="flex flex-wrap items-start gap-stack py-2.5 first:pt-0 last:pb-0"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex flex-wrap items-center gap-inline">
                  <span className="font-medium text-default-900">
                    {t('reports.detail.version', { number: version.versionNumber })}
                  </span>
                  <VersionStatusBadge status={version.status} />
                </span>
                {issued ? (
                  <span className="text-caption text-default-600">
                    {t('reports.detail.issuedBy')}
                    {': '}
                    <PersonAt person={version.issuedBy} at={version.issuedAt} />
                  </span>
                ) : (
                  <span className="text-caption text-default-600">
                    {t('reports.detail.preparedBy')}
                    {': '}
                    <PersonAt person={version.preparedBy} at={version.preparedAt} />
                  </span>
                )}
              </div>
              {issued && report.can.downloadFile ? (
                <VersionFileDownload
                  report={report}
                  versionNumber={version.versionNumber}
                  languages={version.downloadLanguages}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
      {report.can.downloadFile && report.versions.some((version) => ISSUED_EVER.includes(version.status)) ? (
        <p className="mt-stack text-caption text-default-600">{t('reports.detail.downloadHint')}</p>
      ) : null}
    </Panel>
  );
}

function EventsPanel({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();

  return (
    <Panel title={t('reports.detail.events')} description={t('reports.detail.eventsHint')}>
      <ol className="flex flex-col divide-y divide-default-100">
        {report.events.map((event) => (
          <li key={event.id} className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0">
            <span className="flex flex-wrap items-center gap-inline">
              <span className="text-caption text-default-600">
                {t('reports.detail.version', { number: event.versionNumber })}
              </span>
              {event.fromStatus === null ? (
                <span className="text-body text-default-900">{t('reports.detail.creationEvent')}</span>
              ) : (
                <>
                  <span className="text-caption text-default-600">{t('reports.detail.eventFrom')}</span>
                  <VersionStatusBadge status={event.fromStatus} />
                  <span className="text-caption text-default-600">{t('reports.detail.eventTo')}</span>
                </>
              )}
              <VersionStatusBadge status={event.toStatus} />
            </span>
            {event.reason ? (
              <span className="text-caption text-default-800">
                {t('reports.detail.eventReason')}
                {': '}
                <bdi className="whitespace-pre-wrap">{event.reason}</bdi>
              </span>
            ) : null}
            <span className="text-caption text-default-600">
              {t('reports.detail.eventActedBy')}
              {': '}
              <PersonAt person={event.actedBy} at={event.actedAt} />
            </span>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Deliveries and affected parties — recorded by people, never sent by the system
// ---------------------------------------------------------------------------

function DeliveriesPanel({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();

  return (
    <Panel title={t('reports.delivery.title')} description={t('reports.delivery.hint')}>
      <div className="flex flex-col gap-stack">
        <Alert
          tone="info"
          title={t('reports.delivery.systemSendsNothingTitle')}
          description={t('reports.delivery.systemSendsNothingBody')}
        />
        {/* Record a delivery or a notice, from `can.deliver` (sub-phase 10c). */}
        <DeliveryActions report={report} />
        {report.deliveries.length === 0 ? (
          <p className="text-body text-default-600">{t('reports.delivery.none')}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-default-100" data-testid="deliveries">
            {report.deliveries.map((delivery) => (
              <li key={delivery.id} className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0">
                <span className="flex flex-wrap items-center gap-inline">
                  <span className="font-medium text-default-900">
                    {t(`deliveryPurpose.${delivery.purpose}`)}
                  </span>
                  <span className="text-caption text-default-600">
                    {t('reports.detail.version', { number: delivery.versionNumber })}
                  </span>
                </span>
                <span className="text-caption text-default-800">
                  {t(`deliveryMethod.${delivery.method}`)}
                  {' · '}
                  {t('reports.delivery.recipientName')}
                  {': '}
                  <bdi>{delivery.recipientName}</bdi>
                  {' · '}
                  {t('reports.delivery.deliveredAt')}
                  {': '}
                  <MomentText at={delivery.deliveredAt} />
                </span>
                <span className="text-caption text-default-800">
                  {t('reports.delivery.receiptConfirmed')}
                  {': '}
                  {delivery.receiptConfirmed ? t('common.yes') : t('common.no')}
                  {delivery.receiptConfirmed && delivery.receiptEvidence ? (
                    <>
                      {' · '}
                      <bdi className="whitespace-pre-wrap">{delivery.receiptEvidence}</bdi>
                    </>
                  ) : null}
                </span>
                {delivery.note ? (
                  <span className="text-caption text-default-800">
                    {t('reports.delivery.note')}
                    {': '}
                    <bdi className="whitespace-pre-wrap">{delivery.note}</bdi>
                  </span>
                ) : null}
                {/* The actual send result: the system sent nothing. */}
                <span className="text-caption text-default-700">
                  {t('reports.delivery.sentBySystem')}
                  {': '}
                  {t('reports.delivery.sentBySystemValue')}
                </span>
                <span className="text-caption text-default-600">
                  {isolateRuns(
                    t('reports.delivery.recordedBy', { name: run('name'), date: run('date') }),
                    {
                      name: delivery.recordedBy.displayName ?? t('common.notRecorded'),
                      date: <MomentText at={delivery.recordedAt} />,
                    },
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

function AffectedPartiesPanel({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();
  const partyId = useId();

  return (
    <Panel
      title={t('reports.delivery.affectedParties')}
      description={t('reports.delivery.affectedPartiesHint')}
    >
      {report.affectedParties.length === 0 ? (
        <p className="text-body text-default-600">{t('reports.delivery.noAffectedParties')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-default-100" data-testid="affected-parties">
          {report.affectedParties.map((party, index) => (
            <li
              key={`${party.recipientName}-${party.versionNumber}-${index}`}
              data-party={index}
              className="flex flex-wrap items-center gap-inline py-2.5 text-caption text-default-800 first:pt-0 last:pb-0"
            >
              <span className="min-w-0" id={`${partyId}-${index}`}>
                <bdi className="font-medium">{party.recipientName}</bdi>
                {' · '}
                {t('reports.detail.version', { number: party.versionNumber })}
                {' · '}
                <MomentText at={party.deliveredAt} />
              </span>
              {/* A notice to this recipient, from `can.deliver`. The product informs nobody itself. */}
              <NoticeForParty report={report} party={party} describedBy={`${partyId}-${index}`} />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
